// src/components/filters/EvaluationPeriodPicker.js
//
// Reusable Evaluation Period picker component
// ===========================================
//
// PURPOSE
// -------
// This component renders the shared frontend framework for selecting
// report periods in a business-friendly way.
//
// Instead of exposing plain from/to fields immediately, it lets the user choose:
//
//   - Month
//   - Year
//   - Week
//   - Day
//   - Manual
//
// For non-manual modes:
//   - arrow left / right navigates the period
//   - a read-only display field shows the current logical period
//
// For manual mode:
//   - two date inputs appear
//   - arrows are hidden
//   - the display field is intentionally omitted
//
// WHY THIS COMPONENT IS KEPT GENERIC
// ----------------------------------
// The goal is to reuse it in:
//   - IncomeReportPage
//   - CommissionReportPage
//   - other reporting screens later
//
// So this component knows nothing about:
//   - API calls
//   - report data
//   - business totals
//
// It only knows how to render period-selection UI.
//
// IMPORTANT BACKWARD COMPATIBILITY
// --------------------------------
// Earlier versions in the project sometimes passed:
//
//   <EvaluationPeriodPicker period={evaluationPeriod} />
//
// and sometimes:
//
//   <EvaluationPeriodPicker controller={evaluationPeriod} />
//
// To avoid breaking existing screens during migration, this component accepts
// BOTH prop names. Internally they mean the same thing.
//
// NEW UX RULE: single allowed mode
// --------------------------------
// If the hook exposes only ONE available mode, for example:
//
//   options = [ { value: "MONTH", label: "Month" } ]
//
// then showing a dropdown is unnecessary and noisy.
// In that case we render a read-only field with the single mode label.
//
// Example:
// - Commission Report for role "user"
//   -> user should see only "Month"
//   -> but should NOT be able to switch to another type
//

import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import "./EvaluationPeriodPicker.css";

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

const EvaluationPeriodPicker = ({
  period,
  controller,
  disabled = false,
  modeLabel,
  fromLabel,
  toLabel,
  displayLabel,
  className = "",
}) => {
  const { t } = useTranslation(["visits"]);

  const resolvedModeLabel = modeLabel || t("visits:evaluationPeriod.modeLabel");
  const resolvedFromLabel = fromLabel || t("visits:evaluationPeriod.fromLabel");
  const resolvedToLabel = toLabel || t("visits:evaluationPeriod.toLabel");
  const resolvedDisplayLabel = displayLabel || t("visits:evaluationPeriod.displayLabel");

  const modeOptionLabel = (option) =>
    t(`visits:evaluationPeriod.modes.${option?.value}`, {
      defaultValue: option?.label || option?.value || "",
    });

  /**
   * Support both prop names:
   * - period
   * - controller
   *
   * This makes the shared component tolerant during refactors.
   */
  const source = period || controller;

  if (!source) return null;

  const {
    mode,
    options,
    isManual,
    showManualInputs,
    displayLabel: currentDisplayLabel,
    fromDate,
    toDate,
    cursorDate,
    manualFrom,
    manualTo,
    setMode,
    goPrev,
    goNext,
    setManualFrom,
    setManualTo,
  } = source;

  /**
   * If there is only one available mode, render a static read-only field
   * instead of a dropdown.
   */
  const singleModeLabel = useMemo(() => {
    if (!Array.isArray(options) || options.length !== 1) return "";
    return modeOptionLabel(options[0]);
  }, [options, t]);

  const hasSingleMode = Array.isArray(options) && options.length === 1;

  const localizedDisplayLabel = useMemo(() => {
    if (!currentDisplayLabel) return "";

    const locale = t("common:language.locale", { defaultValue: "en-US" });
    const safeCursor = cursorDate instanceof Date ? cursorDate : new Date(cursorDate || Date.now());

    if (mode === "MONTH") {
      return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(safeCursor);
    }

    if ((mode === "THREE_MONTHS" || mode === "SIX_MONTHS") && fromDate && toDate) {
      const formatter = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
      return `${formatter.format(new Date(fromDate))} – ${formatter.format(new Date(toDate))}`;
    }

    if (mode === "DAY") {
      return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "long", year: "numeric" }).format(safeCursor);
    }

    if (mode === "WEEK" && fromDate && toDate) {
      const weekMatch = String(currentDisplayLabel).match(/Week\s+(\d+)/i);
      const week = weekMatch?.[1] || "";
      return t("visits:evaluationPeriod.weekLabel", {
        week,
        year: new Date(fromDate).getFullYear(),
        from: String(source.from || ""),
        to: String(source.to || ""),
      });
    }

    return currentDisplayLabel;
  }, [currentDisplayLabel, cursorDate, fromDate, mode, source, t, toDate]);

  return (
    <div className={`evaluation-period-picker ${className}`.trim()}>
      {/* ------------------------------------------------------------------ */}
      {/* Mode selector or static mode field                                */}
      {/* ------------------------------------------------------------------ */}
      <div className="evaluation-period-picker__group">
        <label className="evaluation-period-picker__label">{resolvedModeLabel}</label>

        {hasSingleMode ? (
          <input
            type="text"
            className="evaluation-period-picker__display"
            value={singleModeLabel}
            readOnly
            disabled={disabled}
          />
        ) : (
          <select
            className="evaluation-period-picker__select"
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={disabled}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {modeOptionLabel(option)}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Non-manual navigation                                              */}
      {/* ------------------------------------------------------------------ */}
      {!showManualInputs && (
        <>
          <div className="evaluation-period-picker__group">
            <label className="evaluation-period-picker__label">&nbsp;</label>

            <div className="evaluation-period-picker__nav">
              <button
                type="button"
                className="evaluation-period-picker__nav-button"
                onClick={goPrev}
                disabled={disabled}
                aria-label={t("visits:evaluationPeriod.previousAriaLabel")}
                title={t("visits:evaluationPeriod.previousTitle")}
              >
                ←
              </button>

              <button
                type="button"
                className="evaluation-period-picker__nav-button"
                onClick={goNext}
                disabled={disabled}
                aria-label={t("visits:evaluationPeriod.nextAriaLabel")}
                title={t("visits:evaluationPeriod.nextTitle")}
              >
                →
              </button>
            </div>
          </div>

          <div className="evaluation-period-picker__group evaluation-period-picker__group--display">
            <label className="evaluation-period-picker__label">{resolvedDisplayLabel}</label>
            <input
              type="text"
              className="evaluation-period-picker__display"
              value={localizedDisplayLabel}
              readOnly
              disabled={disabled}
            />
          </div>
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Manual inputs                                                      */}
      {/* ------------------------------------------------------------------ */}
      {showManualInputs && (
        <div className="evaluation-period-picker__manual">
          <div className="evaluation-period-picker__group">
            <label className="evaluation-period-picker__label">{resolvedFromLabel}</label>
            <input
              type="date"
              className="evaluation-period-picker__input"
              value={manualFrom}
              onChange={(e) => setManualFrom(e.target.value)}
              disabled={disabled}
            />
          </div>

          <div className="evaluation-period-picker__group">
            <label className="evaluation-period-picker__label">{resolvedToLabel}</label>
            <input
              type="date"
              className="evaluation-period-picker__input"
              value={manualTo}
              onChange={(e) => setManualTo(e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Optional helper text                                               */}
      {/* ------------------------------------------------------------------ */}
      {isManual && (
        <div className="evaluation-period-picker__hint">
          {t("visits:evaluationPeriod.manualHint")}
        </div>
      )}
    </div>
  );
};

export default EvaluationPeriodPicker;